import { orderMemory } from "../../Objects/Memory/Memory"
import { OrderEntity } from "../../Objects/Order/OrderEntity"
import { useError } from "../../context/useErrorContext"
import { useLoad } from "../../context/useLoadContext"
import { GetRequest } from "../../services/ApiClientService"
import { API_ROUTES } from "../../configuration/RoutesApi"
import { useTranslation } from "react-i18next"
import { ERequestType } from "../../Enum/ERequestType"
import type { PagedResult } from "../../Objects/Generics/PagedResult"

export const ORDER_LIST_PAGE_SIZE = 30

export interface OrderPaginationOptions {
  pageNumber?: number
  pageSize?: number
  append?: boolean
  searchTerm?: string
  customerName?: string
  customerDocument?: string
  orderCode?: string
}

export interface OrderPaginationState {
  pageNumber: number
  pageSize: number
  totalCount: number
  hasNextPage: boolean
}

export const useGetOrder = () => {
  const { t } = useTranslation("general")

  const { enableLoad, disableLoad, enableFlagLoad, disableFlagLoad } = useLoad()

  const { setError } = useError()

  const handleGetOrder = async (
    onSuccess: (() => void) | null,
    dateStart: string,
    dateEnd?: string,
    rote: string = API_ROUTES.ORDER.GET_ALL,
    requestType: ERequestType = ERequestType.Store,
    onlyOpenLostOrders: boolean = false,
    onlyOpenFulfillmentDelayed: boolean = false,
    paginationOptions: OrderPaginationOptions = {},
    suppressGlobalLoading: boolean = false,
  ): Promise<OrderPaginationState | null> => {
    if (!suppressGlobalLoading) {
      enableLoad()
    }

    if (!suppressGlobalLoading) {
      enableFlagLoad()
    }

    try {
      const params = new URLSearchParams()

      if (dateStart.trim()) {
        params.append("dateStart", dateStart)
      }

      if (dateEnd) {
        params.append("dateEnd", dateEnd)
      }

      if (onlyOpenLostOrders) {
        params.append("onlyOpenLostOrders", "true")
      }

      if (onlyOpenFulfillmentDelayed) {
        params.append("onlyOpenFulfillmentDelayed", "true")
      }

      const pageNumber = paginationOptions.pageNumber ?? 1

      const pageSize = paginationOptions.pageSize ?? ORDER_LIST_PAGE_SIZE

      params.append("pageNumber", pageNumber.toString())
      params.append("pageSize", pageSize.toString())

      if (paginationOptions.searchTerm?.trim()) {
        params.append("searchTerm", paginationOptions.searchTerm.trim())
      }

      if (paginationOptions.customerName?.trim()) {
        params.append("customerName", paginationOptions.customerName.trim())
      }

      if (paginationOptions.customerDocument?.trim()) {
        params.append("customerDocument", paginationOptions.customerDocument.trim())
      }

      if (paginationOptions.orderCode?.trim()) {
        params.append("orderCode", paginationOptions.orderCode.trim())
      }

      const response = await GetRequest<OrderEntity[] | PagedResult<OrderEntity>>(`${rote}?${params.toString()}`, requestType)

      if (response.success) {
        const responseData = response.data

        let rawOrders: OrderEntity[] = []

        let paginationState: OrderPaginationState = {
          pageNumber,
          pageSize,
          totalCount: 0,
          hasNextPage: false,
        }

        if (Array.isArray(responseData)) {
          rawOrders = responseData

          paginationState = {
            pageNumber,
            pageSize,
            totalCount: responseData.length,
            hasNextPage: false,
          }
        } else if (responseData) {
          rawOrders = responseData.items

          paginationState = {
            pageNumber: responseData.pageNumber,
            pageSize: responseData.pageSize,
            totalCount: responseData.totalCount,
            hasNextPage: responseData.hasNextPage,
          }
        }

        const orders = rawOrders.map((e) => new OrderEntity(e))

        orderMemory.setList(paginationOptions.append ? [...orderMemory.listData, ...orders] : orders)

        onSuccess?.()

        return paginationState
      }

      throw new Error(response.message)
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : t("label_unknown_error_description")
      setError(t("label_title_erro"), message)

      return null
    } finally {
      if (!suppressGlobalLoading) {
        disableLoad()
      }

      if (!suppressGlobalLoading) {
        disableFlagLoad()
      }
    }
  }

  return {
    handleGetOrder,
  }
}
